/**
 * 화면 구성
 * ---------------------------------------------------------------------------
 * 제품에는 로그인 화면이 없다. 세션을 못 받으면 플랫폼 로그인으로 보낸다.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Alert, Button, Spin } from 'antd';
import axios from 'axios';
import { AppLayout } from './components/AppLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useSession } from './lib/session';
import { goToPlatformLogin } from './lib/auth';
import { DailyLogListPage } from './pages/DailyLogListPage';
import { DailyLogEditorPage } from './pages/DailyLogEditorPage';
import { SiteListPage } from './pages/SiteListPage';
import { FacilityPage } from './pages/FacilityPage';
import { SelfMeasurementPage } from './pages/SelfMeasurementPage';
import { DashboardPage } from './pages/DashboardPage';
import { MyAccountPage } from './pages/MyAccountPage';
import { AiReportListPage } from './pages/AiReportListPage';

export function App() {
  const location = useLocation();
  const { data: session, isLoading, isError, error } = useSession();

  // 제품 API 가 내려가 있는 것과 로그인이 없는 것은 다르다.
  // 전자를 로그인 문제로 안내하면 플랫폼과 제품 사이를 계속 오가게 된다.
  const apiDown = isApiUnreachable(error);

  useEffect(() => {
    if (isError && !apiDown) goToPlatformLogin();
  }, [isError, apiDown]);

  if (isLoading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="불러오는 중" />
      </div>
    );
  }

  if (apiDown) {
    return (
      <div style={{ padding: 48 }}>
        <Alert
          type="error"
          showIcon
          message="WORK&ECO 서버에 연결할 수 없습니다"
          description={
            <>
              제품 API 가 실행 중인지 확인해 주세요.
              <br />
              <code>cd 03.WORKNECO &amp;&amp; npm run dev</code>
            </>
          }
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              다시 시도
            </Button>
          }
        />
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ padding: 48 }}>
        <Alert
          type="warning"
          showIcon
          message="로그인이 필요합니다"
          description="WORKnHub 플랫폼에서 로그인한 뒤 다시 시도해 주세요."
          action={
            <Button size="small" type="primary" onClick={() => goToPlatformLogin()}>
              로그인하러 가기
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <AppLayout session={session}>
      {/*
        일지 경로에 유형이 들어 있다. 메뉴가 유형별로 나뉘었으므로 주소만
        봐도 어느 메뉴인지 알 수 있어야 하고, 즐겨찾기도 그래야 쓸모가 있다.

        경계로 감싼다. 한 화면이 터졌다고 앱 전체가 하얗게 비면 사용자는
        무엇이 잘못됐는지 말할 수 없다. 경로가 바뀌면 경계가 다시 풀린다.
      */}
      <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          {/* 대시보드가 홈이다. 들어오자마자 이번 달에 빠진 날을 본다. */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/logs/:logType" element={<DailyLogListPage />} />
          <Route path="/logs/:logType/new" element={<DailyLogEditorPage />} />
          <Route path="/logs/:logType/:dailyLogId" element={<DailyLogEditorPage />} />
          <Route path="/self-measurements" element={<SelfMeasurementPage />} />
          <Route path="/sites" element={<SiteListPage />} />
          <Route path="/sites/:siteId/facilities" element={<FacilityPage />} />
          {/*
            내 정보. WORK&Hub 콘솔이 기업 관리자 전용이 되면서, 일반
            사용자가 비밀번호를 바꿀 수 있는 유일한 자리가 됐다.
          */}
          {/* 만든 AI 보고서 보관함. 다시 보는 데는 요금이 들지 않는다. */}
          <Route path="/ai-reports" element={<AiReportListPage />} />
          <Route path="/me" element={<MyAccountPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ErrorBoundary>
    </AppLayout>
  );
}

/**
 * 제품 API 가 내려가 있는가.
 *
 * vite 프록시는 대상이 죽어 있으면 502 에 `API_UNREACHABLE` 를 담아 준다.
 * 운영에서는 프록시가 없으므로 네트워크 오류(response 없음)로 온다.
 */
function isApiUnreachable(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (!error.response) return true;
  if (error.response.status !== 502 && error.response.status !== 503) return false;
  return true;
}
